/**
 * Manage companies data
 */
import Ajax from '@/lib/ajax_wrapper'
import {authHeader, request} from '@/lib/request'

function sitesPath(companyId) {
  return `/admin/companies/${companyId}/sites`
}

/**
 * Read paginated company sites
 *
 * @param {string} companyId
 * @param {object} page
 * @param {integer} page.number
 * @param {integer} page.pePage
 *
 * @returns {promise}
 */
export function readCompanySitesData(companyId, page) {
  return Ajax.request({
    url: sitesPath(companyId),
    page: page
  })
}

/**
 * Create a company site
 *
 * @param {string} companyId
 * @param {object} site
 * @param {string} site.name
 *
 * @returns {promise}
 */
export function createCompanySiteData(companyId, site) {
  return Ajax.request({
    url: sitesPath(companyId),
    method: 'POST',
    data: {site}
  })
}

/**
 * Create a populated company site
 *
 * @param {string} companyId
 * @param {object} site
 * @param {string} site.name
 *
 * @returns {promise}
 */
export function createPopulatedCompanySiteData(data) {
  return Ajax.request({
    url: '/sites/create_website',
    method: 'POST',
    data: data
  })
}

/**
 * Read a company site
 *
 * @param {string} companyId
 * @param {string} id
 *
 * @returns {promise}
 */
export function readCompanySiteData(companyId, id) {
  return Ajax.request({
    url: `${sitesPath(companyId)}/${id}`
  })
}

/**
 * Update a company site
 *
 * @param {string} companyId
 * @param {string} id
 * @param {object} company
 * @param {string} company.name
 *
 * @returns {promise}
 */
export function updateCompanySiteData(companyId, id, site) {
  return Ajax.request({
    url: `${sitesPath(companyId)}/${id}`,
    method: 'PATCH',
    data: {site}
  })
}

export function updateCompanySiteLogoImage(companyId, siteId, data, token) {
  const method = 'PATCH'
  const url = `/${sitesPath(companyId)}/${siteId}/logo`
  const headers = authHeader(token)

  return request({data, headers, method, url})
    .then((xhr) => {
      return JSON.parse(xhr.responseText)
    })
    .catch((xhr) => {
      return JSON.parse(xhr.responseText)
    })
}

/**
 * Delete a company site logo
 *
 * @param {string} companyId
 * @param {string} id
 *
 * @returns {promise}
 */
export function deleteCompanySiteLogoImage(companyId, siteId) {
  return Ajax.request({
    url: `${sitesPath(companyId)}/${siteId}/logo`,
    method: 'DELETE'
  })
}

export function updateCompanySiteFaviconImage(companyId, siteId, data, token) {
  const method = 'PATCH'
  const url = `/${sitesPath(companyId)}/${siteId}/favicon`
  const headers = authHeader(token)

  return request({data, headers, method, url})
    .then((xhr) => {
      return JSON.parse(xhr.responseText)
    })
    .catch((xhr) => {
      return JSON.parse(xhr.responseText)
    })
}

/**
 * Delete a company site favicon
 *
 * @param {string} companyId
 * @param {string} id
 *
 * @returns {promise}
 */
export function deleteCompanySiteFaviconImage(companyId, siteId) {
  return Ajax.request({
    url: `${sitesPath(companyId)}/${siteId}/favicon`,
    method: 'DELETE'
  })
}

/**
 * Delete a company site
 *
 * @param {string} companyId
 * @param {string} id
 *
 * @returns {promise}
 */
export function deleteCompanySiteData(companyId, id) {
  return Ajax.request({
    url: `${sitesPath(companyId)}/${id}`,
    method: 'DELETE'
  })
}

/**
 * Read paginated sites for moderation
 *
 * @param {object} page
 * @param {integer} page.number
 * @param {integer} page.pePage
 *
 * @returns {promise}
 */
export function readSitesData(page, query) {
  return Ajax.request({
    url: `/admin/sites${query}`,
    page: page
  })
}

/**
 * Moderate a website
 *
 * @returns {promise}
 */
export function moderateSiteData(siteId, action) {
  return Ajax.request({
    url: `/admin/sites/${siteId}/${action}`,
    method: 'POST'
  })
}
